/**
 * Debit order mandates — the record FNB asked for.
 *
 *   "be able to reproduce the records of mandate permissions given by clients
 *    and their digital signatures given, with an audit trail and all attached
 *    user identities, and be able to export that from the Portal in case the
 *    bank needs, and also be able to download newly signed mandates in csv
 *    format ... in order to add it to our collections dashboard on FNB online
 *    banking enterprise."
 *
 * TWO THINGS ON ONE SCREEN, AND THEY ARE DELIBERATELY NOT THE SAME SHAPE.
 *
 *   THE EVIDENCE PACK answers a dispute: did this person agree, and to what.
 *   It carries the signature, the wording they were shown, where they signed
 *   from and every event since -- and no bank details beyond a last4.
 *
 *   THE EXPORT answers the bank: here are the mandates to load. It DECRYPTS,
 *   and it is the only thing on the platform that does.
 *
 * THE EXPORT IS DRAWN AS THE HEAVIER OF THE TWO, because it is. It sits under
 * its own heading, states what it is about to do before it does it, and cannot
 * be pressed without a reason -- which the database requires anyway and writes
 * to every affected mandate's history BEFORE returning a single number.
 *
 * WHY THE FILE IS BUILT HERE RATHER THAN FETCHED
 *
 * There is no "download the CSV" endpoint, and there should not be one: that
 * would be a URL which, if it ever leaked into a log or a referrer, hands over
 * decrypted bank details. The rows arrive through the same audited RPC as
 * everything else and the browser assembles the file from them, so the only
 * thing that ever crosses the wire is an authenticated, reason-stamped call.
 *
 * NOTHING IS LEFT ON SCREEN AFTERWARDS. The decrypted rows are dropped from
 * component state the moment the file is handed over. A finance officer who
 * walks away from a desk should not leave three hundred account numbers on it.
 */

import { useState } from 'react';
import {
  O, SANS, DISPLAY, Card, Field, Notice, PrimaryCTA, StatusPill, textInputStyle,
} from '../ui';
import {
  evidencePack, exportForTheBank, toCsv, downloadCsv, csvFilename, CSV_COLUMNS,
} from '../lib/mandates';

const stamp = (v) => (v ? new Date(v).toLocaleString('en-ZA') : '—');

const Row = ({ label, value, mono = false }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', gap: 16,
    padding: '8px 0', borderBottom: `1px solid ${O.hairline}`,
  }}>
    <span style={{ fontFamily: SANS, fontSize: 12, color: O.inkMuted, flexShrink: 0 }}>
      {label}
    </span>
    <span style={{
      fontFamily: mono ? 'ui-monospace,SFMono-Regular,Menlo,monospace' : SANS,
      fontSize: 12, fontWeight: 600, color: O.ink, textAlign: 'right', wordBreak: 'break-word',
    }}>{value ?? '—'}</span>
  </div>
);

// ---------------------------------------------------------------------------

/**
 * THE PACK, FOR A DISPUTE.
 *
 * A mandate reference rather than a name search, deliberately. Prohibition 6:
 * "never a person's name and never a personal code in search." A dispute always
 * arrives carrying a reference -- the bank quotes it, and it is printed on the
 * member's own screen -- so nothing here needs to be able to find a person.
 */
const EvidencePack = () => {
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const draw = async () => {
    setBusy(true);
    setFailure(null);
    setPack(null);
    const { data, error } = await evidencePack(reference.trim(), reason.trim());
    setBusy(false);
    if (error) { setFailure(error); return; }
    if (!data) { setFailure('No mandate with that reference.'); return; }
    setPack(data);
  };

  return (
    <Card style={{ marginBottom: 18 }}>
      <p style={{
        fontFamily: DISPLAY, fontSize: 17, fontWeight: 700, color: O.ink, margin: '0 0 4px',
      }}>Evidence pack</p>
      <p style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted, margin: '0 0 14px', lineHeight: 1.65 }}>
        Everything about how one mandate was agreed to: the signature, the exact wording that
        person was shown, where they signed from and every event since. No bank details beyond
        the last four digits — a dispute is about whether somebody agreed, not about their
        account number.
      </p>

      {failure && <Notice tone="critical">{failure}</Notice>}

      <Field label="Mandate reference" htmlFor="pack-ref" hint="The OFXDO- reference the bank or the member quotes.">
        <input
          id="pack-ref" type="text" value={reference}
          onChange={(e) => setReference(e.target.value)}
          style={textInputStyle(false)} autoComplete="off"
        />
      </Field>

      <Field
        label="Why you are opening it"
        htmlFor="pack-reason"
        hint="Written to this mandate's own history, against your OID, permanently."
      >
        <input
          id="pack-reason" type="text" value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={textInputStyle(false)} autoComplete="off"
        />
      </Field>

      <PrimaryCTA onClick={draw} busy={busy} disabled={busy || !reference.trim() || !reason.trim()}>
        {busy ? 'Opening…' : 'Open the pack'}
      </PrimaryCTA>

      {pack && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10, flexWrap: 'wrap' }}>
            <p style={{ fontFamily: SANS, fontSize: 13.5, fontWeight: 700, color: O.ink, margin: 0 }}>
              {pack.mandate_reference}
            </p>
            <StatusPill tone={pack.status === 'signed' ? 'green' : 'amber'}>
              {pack.status}
            </StatusPill>
          </div>

          <Row label="Signed as" value={pack.signed_as} />
          <Row label="Account holder" value={pack.account_holder_name} />
          <Row label="Orchid identity" value={pack.ofx_connect_code} mono />
          <Row label="Account" value={`${pack.bank_name || '—'} ···· ${pack.account_last4 || '—'}`} />
          <Row label="Authorises up to" value={`R${Number(pack.amount_ceiling_zar || 0).toFixed(2)} a month`} />
          <Row label="Signed" value={stamp(pack.signed_at)} />
          <Row label="How" value={pack.signature_method} />
          {pack.signature_typed_name && (
            <Row label="Typed as" value={pack.signature_typed_name} />
          )}

          {/* THE TICK BOX, WHERE THERE IS ONE. On the typed path this is the
              evidence -- "they typed a name" is weak, "they were shown R799 on
              the 25th and affirmed it" is what a dispute turns on. */}
          {pack.typed_consent_confirmed && (
            <div style={{
              margin: '12px 0', padding: 12, borderRadius: 10,
              background: 'rgba(13,61,42,0.04)', border: `1px solid ${O.hairlineOn}`,
            }}>
              <p style={{
                fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 6px',
              }}>What they agreed to, and when</p>
              <p style={{ fontFamily: SANS, fontSize: 12, color: O.ink, margin: 0, lineHeight: 1.65 }}>
                {pack.typed_consent_text}
              </p>
              <p style={{ fontFamily: SANS, fontSize: 11, color: O.inkFaint, margin: '6px 0 0' }}>
                Confirmed {stamp(pack.typed_consent_at)}
              </p>
            </div>
          )}

          <Row label="Signed at" value={pack.signed_place} />
          <Row label="From" value={pack.signed_ip ? String(pack.signed_ip) : '—'} mono />
          <Row label="Device" value={pack.signed_user_agent} />
          <Row label="Wording version" value={pack.mandate_text_version} />
          <Row label="Wording fingerprint" value={pack.mandate_text_sha256} mono />

          <p style={{
            fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: O.inkFaint, margin: '18px 0 8px',
          }}>History</p>
          {(pack.history || []).length === 0 ? (
            <p style={{ fontFamily: SANS, fontSize: 12, color: O.inkMuted, margin: 0 }}>
              Nothing recorded yet.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 460 }}>
                <tbody>
                  {pack.history.map((e, i) => (
                    <tr key={`${e.event}-${e.at}-${e.by || i}`} style={{ borderBottom: `1px solid ${O.hairline}` }}>
                      <td style={{ fontFamily: SANS, fontSize: 12, padding: '7px 10px 7px 0', fontWeight: 600 }}>
                        {e.event}
                      </td>
                      <td style={{ fontFamily: SANS, fontSize: 12, padding: '7px 10px', color: O.inkMuted, whiteSpace: 'nowrap' }}>
                        {stamp(e.at)}
                      </td>
                      <td style={{ fontFamily: SANS, fontSize: 12, padding: '7px 10px', color: O.inkMuted }}>
                        {e.by}
                      </td>
                      <td style={{ fontFamily: SANS, fontSize: 12, padding: '7px 0', color: O.inkMuted }}>
                        {e.reason || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------

/**
 * THE EXPORT, FOR THE BANK.
 *
 * The one thing on the platform that decrypts. Everything about how this is
 * drawn is meant to make that plain to whoever presses it, rather than making
 * it feel like any other download.
 */
const BankExport = () => {
  const [reason, setReason] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [done, setDone] = useState(null);

  const run = async () => {
    setBusy(true);
    setFailure(null);
    setDone(null);

    const { data, error } = await exportForTheBank({
      reason: reason.trim(),
      from: from || null,
      to: to || null,
    });
    setBusy(false);
    if (error) { setFailure(error); return; }

    if (!data.length) {
      // NOT AN ERROR. "No new mandates this week" is the commonest answer once
      // the founding cohort has signed, and reporting it as a failure would
      // teach somebody to re-run it until something appears.
      setDone('No mandates were signed in that period. Nothing to send.');
      return;
    }

    downloadCsv(toCsv(data), csvFilename());
    setDone(`${data.length} mandate${data.length === 1 ? '' : 's'} downloaded. `
          + 'The decryption is on each mandate’s history against your OID.');
    // NOTHING IS KEPT. The rows held real account numbers; they are gone from
    // this component the instant the file exists.
    setReason('');
  };

  return (
    <Card>
      <p style={{
        fontFamily: DISPLAY, fontSize: 17, fontWeight: 700, color: O.ink, margin: '0 0 4px',
      }}>Send mandates to FNB</p>
      <p style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted, margin: '0 0 12px', lineHeight: 1.65 }}>
        Downloads newly signed mandates as a CSV for the collections dashboard on FNB online
        banking enterprise.
      </p>

      {/* SAID BEFORE IT HAPPENS, not in a tooltip afterwards. */}
      <div style={{
        padding: 13, borderRadius: 11, marginBottom: 14,
        background: 'rgba(168,53,42,0.05)', border: '1px solid rgba(168,53,42,0.22)',
      }}>
        <p style={{ fontFamily: SANS, fontSize: 12, color: O.ink, margin: 0, lineHeight: 1.7 }}>
          <strong>This decrypts real ID numbers and real bank account numbers.</strong> It is the
          only thing on Orchid that does. Every mandate in the file gets a line in its own history
          naming you and the reason you give below — written before the file is built, so a
          decryption that happened is on the record whether or not the download reached anybody.
        </p>
      </div>

      {failure && <Notice tone="critical">{failure}</Notice>}
      {done && !failure && <Notice tone="info">{done}</Notice>}

      <Field
        label="Why you are sending them"
        htmlFor="exp-reason"
        hint="Written to every mandate in the file, against your OID, permanently."
      >
        <input
          id="exp-reason" type="text" value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={textInputStyle(false)} autoComplete="off"
        />
      </Field>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 180px' }}>
          <Field label="Signed from" htmlFor="exp-from" hint="Usually the last upload.">
            <input id="exp-from" type="date" value={from}
                   onChange={(e) => setFrom(e.target.value)} style={textInputStyle(false)} />
          </Field>
        </div>
        <div style={{ flex: '1 1 180px' }}>
          <Field label="to" htmlFor="exp-to" hint="Included. Leave blank for today.">
            <input id="exp-to" type="date" value={to}
                   onChange={(e) => setTo(e.target.value)} style={textInputStyle(false)} />
          </Field>
        </div>
      </div>

      {/* A PERIOD IS THE DEFAULT POSTURE. Exporting the whole book every time
          somebody wants this week's five new mandates is a decryption of every
          member's bank details to answer a question about five rows. */}
      {!from && (
        <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.warning, margin: '0 0 12px', lineHeight: 1.6 }}>
          With no start date this decrypts every mandate Orchid holds. Set one unless you mean to.
        </p>
      )}

      <PrimaryCTA onClick={run} busy={busy} disabled={busy || !reason.trim()}>
        {busy ? 'Preparing…' : 'Decrypt and download'}
      </PrimaryCTA>

      <details style={{ marginTop: 14 }}>
        <summary style={{
          fontFamily: SANS, fontSize: 12, fontWeight: 600, color: O.green, cursor: 'pointer',
        }}>
          What is in the file
        </summary>
        <ul style={{
          fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, lineHeight: 1.8,
          margin: '8px 0 0', paddingLeft: 18,
        }}>
          {CSV_COLUMNS.map(([key, label]) => <li key={key}>{label}</li>)}
        </ul>
      </details>
    </Card>
  );
};

// ---------------------------------------------------------------------------

export default function DebitOrderMandates({ permissions = [] }) {
  // Read from what the database said this account holds on this card, rather
  // than from anything decided here.
  const can = (key) => (permissions || []).some((p) => p.permission_key === key);

  // TWO PERMISSIONS, TWO HALVES, AND THE SECOND IS THE HEAVIER ONE.
  //
  // view_debit_order_mandates opens a pack; export_debit_order_mandates
  // decrypts. Canon 4.3 refuses to grant the export directly at all -- it has
  // to come through a role with an expiry -- so somebody holding the first and
  // not the second is the ordinary case rather than an edge one, and this
  // screen draws only their half rather than showing them a button that would
  // refuse.
  const mayView = can('view_debit_order_mandates');
  const mayExport = can('export_debit_order_mandates');

  if (!mayView && !mayExport) {
    return (
      <Notice tone="info">
        Your account is not permitted to read debit order mandates.
      </Notice>
    );
  }

  return (
    <div>
      {mayView && <EvidencePack />}
      {mayExport && <BankExport />}
      {mayView && !mayExport && (
        <p style={{ fontFamily: SANS, fontSize: 11.5, color: O.inkFaint, margin: '4px 0 0', lineHeight: 1.65 }}>
          Sending mandates to the bank decrypts account numbers, and that permission is granted
          for a fixed period through a role rather than held permanently. Ask Human Capital if you
          need it.
        </p>
      )}
    </div>
  );
}

export { EvidencePack, BankExport };
